import { ref, type Ref } from 'vue'

export class AppVisibility {
  #browserPrefixes: string[] = ['moz', 'ms', 'o', 'webkit']
  #hiddenPropertyName: string

  isVisible: Ref<boolean>
  visibilityEventName: string

  constructor () {
    this.isVisible = ref(true)
    this.#hiddenPropertyName = this.getHiddenPropertyName(this.browserPrefix)
    this.visibilityEventName = (this.browserPrefix || '') + 'visibilitychange'
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)
  }

  get browserPrefix (): string | null {
    const browserPrefixes = this.#browserPrefixes
    for (let i = 0; i < browserPrefixes.length; i++) {
      if (this.getHiddenPropertyName(browserPrefixes[i]) in document) {
        // return vendor prefix
        return browserPrefixes[i]
      }
    }

    // no vendor prefix needed
    return null
  }

  getHiddenPropertyName (prefix: string | null): string {
    return (prefix ? prefix + 'Hidden' : 'hidden')
  }

  onVisible (): void {
    // prevent double execution
    if (this.isVisible.value) {
      return
    }

    // change flag value
    this.isVisible.value = true
  }

  onHidden (): void {
    // prevent double execution
    if (!this.isVisible.value) {
      return
    }

    // change flag value
    this.isVisible.value = false
  }

  handleVisibilityChange (forcedFlag?: boolean | Event): void {
    // forcedFlag is a boolean when this event handler is triggered by a
    // focus or blur event otherwise it's an Event object
    if (typeof forcedFlag === 'boolean') {
      if (forcedFlag) {
        return this.onVisible()
      }

      return this.onHidden()
    }

    const hiddenFlag = (document as unknown as Record<string, boolean>)[this.#hiddenPropertyName]
    if (hiddenFlag) {
      return this.onHidden()
    }

    return this.onVisible()
  }

  onFocus (): void {
    this.handleVisibilityChange(true)
  }

  onBlur (): void {
    this.handleVisibilityChange(false)
  }

  init (): void {
    document.addEventListener(this.visibilityEventName, this.handleVisibilityChange, false)

    // extra event listeners for better behaviour
    document.addEventListener('focus', this.onFocus, false)
    document.addEventListener('blur', this.onBlur, false)
    window.addEventListener('focus', this.onFocus, false)
    window.addEventListener('blur', this.onBlur, false)
  }

  destroy (): void {
    document.removeEventListener(this.visibilityEventName, this.handleVisibilityChange, false)

    // extra event listeners for better behaviour
    document.removeEventListener('focus', this.onFocus, false)
    document.removeEventListener('blur', this.onBlur, false)
    window.removeEventListener('focus', this.onFocus, false)
    window.removeEventListener('blur', this.onBlur, false)
  }
}
